// rtdbBaseApi.jsx
import { get, onValue, ref, remove, set, update } from "firebase/database";
import { useEffect, useMemo, useState } from "react";

import { rtdb } from "../corvusFirebase";

export function getRtdbConsoleUrl(path) {
    return `https://console.firebase.google.com/u/0/project/corvus-backend/database/corvus-backend-default-rtdb/data/${path}`;
}

/**
 * RTDB path definitions.
 *
 * These paths are also consumed by the generate-rtdb-wrappers.js script to
 * automatically generate document APIs and React hooks.
 *
 * Rules:
 * - Path names must end with "Document".
 * - Arguments must be declared explicitly in the function signature.
 * - The function must return a path array.
 *
 * Multi-document hooks:
 * - For generated `useXxxRtdbDocuments()` hooks, only the first argument is
 *   treated as the varying document identifier and will be pluralized.
 * - Any remaining arguments are treated as fixed path arguments and are
 *   passed unchanged to every document lookup.
 *
 * Example:
 *
 *   droneWidgetDataDocument: (droneId, widgetName) =>
 *     ['droneStates', droneId, 'widgets', widgetName, 'data']
 *
 * Generates:
 *
 *   useDroneWidgetDataRtdbDocuments(
 *     droneIds,
 *     widgetName,
 *     options
 *   )
 *
 * which internally subscribes to:
 *
 *   droneWidgetDataDocument(droneId, widgetName)
 *
 * for each droneId in droneIds.
 */
export const rtdbPaths = {
    droneStateDocument: (droneId) => ["droneStates", droneId],
    droneConnectionDocument: (droneId) => [
        "droneStates",
        droneId,
        "connection",
    ],
    droneWidgetsDocument: (droneId) => ["droneStates", droneId, "widgets"],
    droneWidgetDocument: (droneId, widgetName) => [
        "droneStates",
        droneId,
        "widgets",
        widgetName,
    ],
    droneWidgetDataDocument: (droneId, widgetName) => [
        "droneStates",
        droneId,
        "widgets",
        widgetName,
        "data",
    ],
    droneErrorsDocument: (droneId) => ["droneStates", droneId, "errors"],
    droneMissionsDocument: (droneId) => ["droneStates", droneId, "missions"],
};

function getRtdbRef(pathArray) {
    return ref(rtdb, pathArray.join("/"));
}

function wrapWithLogging(refObj, path) {
    const log = (method, extra = "") =>
        console.debug(`[RTDB] ${method} → ${path}${extra}`);

    const logError = (method, err) =>
        console.error(`[RTDB] ${method} ✖ ${path}`, err);

    return { refObj, path, log, logError };
}

export function createRtdbDocumentApi(pathFn) {
    return (...args) => {
        const pathArray = pathFn(...args);
        const path = pathArray.join("/");
        const { refObj, log, logError } = wrapWithLogging(
            getRtdbRef(pathArray),
            path,
        );

        return {
            path,

            get: async () => {
                log("get");
                try {
                    const snap = await get(refObj);
                    return snap.exists() ? snap.val() : null;
                } catch (err) {
                    logError("get", err);
                    throw err;
                }
            },

            subscribe: (cb, onError) => {
                log("subscribe");
                return onValue(
                    refObj,
                    (snap) => {
                        log(
                            "onValue",
                            snap.exists() ? "" : " (does not exist)",
                        );
                        cb(snap.exists() ? snap.val() : null);
                    },
                    (err) => {
                        logError("subscribe", err);

                        if (onError) {
                            onError(err);
                        }
                    },
                );
            },

            set: async (data) => {
                log("set");
                try {
                    await set(refObj, data);
                } catch (err) {
                    logError("set", err);
                    throw err;
                }
            },

            update: async (updates) => {
                log("update");
                try {
                    await update(refObj, updates);
                } catch (err) {
                    logError("update", err);
                    throw err;
                }
            },

            delete: async () => {
                log("delete");
                try {
                    await remove(refObj);
                } catch (err) {
                    logError("delete", err);
                    throw err;
                }
            },
        };
    };
}

export function useRtdbDocuments(
    documentApiFn,
    ids = [],
    args = [],
    options = {},
) {
    const { mode = "live", type = "list" } = options;

    // biome-ignore lint/correctness/useExhaustiveDependencies: Recompute normalized IDs when their values change, not when only the array identity changes.
    const memoIds = useMemo(
        () =>
            [...new Set(ids)]
                .filter((id) => id !== undefined && id !== null)
                .sort(),
        [JSON.stringify(ids)],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Stabilize args by value to avoid resubscribing when callers create equivalent arrays.
    const memoArgs = useMemo(() => args, [JSON.stringify(args)]);
    const [data, setData] = useState(type === "map" ? {} : []);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState();

    useEffect(() => {
        if (mode !== "live" && mode !== "once") {
            throw new Error(
                `Invalid mode "${mode}" - expected "live" or "once"`,
            );
        }

        if (type !== "list" && type !== "map") {
            throw new Error(
                `Invalid type "${type}" - expected "list" or "map"`,
            );
        }

        if (memoArgs.some((arg) => arg === undefined || arg === null)) {
            console.debug(
                "[RTDB] useRtdbDocuments → skipping fetch, args contain undefined or null",
                memoArgs,
            );
            return;
        }

        if (memoIds.length === 0) {
            setData(type === "map" ? {} : []);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(undefined);

        const values = {};
        const received = new Set();
        let isCancelled = false;

        const emit = () => {
            if (isCancelled) return;

            if (type === "map") {
                setData({ ...values });
            } else {
                setData(memoIds.map((id) => values[id]).filter(Boolean));
            }
        };

        if (mode === "live") {
            const unsubs = memoIds.map((id) => {
                const api = documentApiFn(id, ...memoArgs);

                return api.subscribe(
                    (doc) => {
                        if (isCancelled) return;

                        if (doc === null) {
                            delete values[id];
                        } else {
                            values[id] = doc;
                        }

                        received.add(id);
                        emit();

                        if (received.size >= memoIds.length) {
                            setIsLoading(false);
                        }
                    },
                    (err) => {
                        if (isCancelled) return;

                        setError(err);
                        setIsLoading(false);
                    },
                );
            });

            return () => {
                isCancelled = true;
                unsubs.forEach((unsub) => {
                    unsub?.();
                });
            };
        }

        Promise.all(
            memoIds.map(async (id) => {
                const doc = await documentApiFn(id, ...memoArgs).get();
                return [id, doc];
            }),
        )
            .then((results) => {
                if (isCancelled) return;

                for (const [id, doc] of results) {
                    if (doc === null) {
                        delete values[id];
                    } else {
                        values[id] = doc;
                    }
                }

                emit();
                setError(undefined);
            })
            .catch((err) => {
                if (isCancelled) return;
                setError(err);
            })
            .finally(() => {
                if (isCancelled) return;
                setIsLoading(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [documentApiFn, memoIds, memoArgs, mode, type]);

    return [data, isLoading, error];
}

export function useRtdbConnected() {
    const [connected, setConnected] = useState(false);

    useEffect(() => {
        const connectedRef = ref(rtdb, ".info/connected");

        return onValue(connectedRef, (snap) => {
            setConnected(snap.val() === true);
        });
    }, []);

    return connected;
}

export function useRtdbServerTimeOffset() {
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        const offsetRef = ref(rtdb, ".info/serverTimeOffset");

        return onValue(offsetRef, (snap) => {
            setOffset(snap.val() || 0);
        });
    }, []);

    return offset;
}
